import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { createPortal } from "react-dom";
import { ReactComponent as DeleteIcon } from "../../../../assets/svgs/delete.svg";
import {
  Table,
  Input,
  Select,
  Button,
  Tag,
  Avatar,
  Card,
  Row,
  Col,
  Space,
  Modal,
  Checkbox,
  Divider,
  Alert,
  Spin,
} from "antd";
import dayjs from "dayjs";
import {
  SearchOutlined,
  PlusOutlined,
  RightOutlined,
  UserOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ThunderboltOutlined,
  ThunderboltFilled,
  UpOutlined,
  DownOutlined,
  EditOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { postApi, getApi, putApi, deleteApi } from "../../../../api/api_instance";
import { API_URLS } from "../../../../api/apiUrls";
import PopupCard from "../../../../components/Cards/PopupCard/PopupCard";
import CardsBorder from "../../../../components/Cards/CardsBorder";
import {
  getAllClients,
  fetchCustomerById,
} from "../../../../redux/customer/customerSlice";
const { Option } = Select;

// Mock categories moved to local state or removed if fully dynamic
const PERMISSION_MODULES = [
  { label: "Dashboard", key: "dashbaord" },
  { label: "Emission Tracking", key: "emission-tracking" },
  { label: "Environmental Impact", key: "environmental-impact" },
  { label: "Social", key: "social" },
  { label: "Governance", key: "governance" },
  { label: "Supply Chain", key: "supply-chain" },
  { label: "SDG", key: "sdg" },
  { label: "Project", key: "project" },
  { label: "Standard Form", key: "standard-forms" },
  { label: "Dynamics Form", key: "dynamic-forms" },
  { label: "User Management", key: "usermanagement" },
  { label: "Roles and Permissions", key: "roles-and-permissions" },
  { label: "My Profile", key: "profile" },
  { label: "License & Access", key: "license-access" },
  // { label: "Location User", key: "location-user" },
  { label: "My Question Bank", key: "question-bank" },
  { label: "Organization Setup", key: "org-setup" },
  { label: "Support", key: "support" },
  // { label: "Individual footprint calculator", key: "footprint" },
];

const AssignSubAdminModal = ({
  open,
  onCancel,
  refresh,
  editData,
  clientId,
  refreshStats,
  projects,
  customerStats,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [selectedProjects, setSelectedProjects] = useState([]);
  const [permissions, setPermissions] = useState(
    PERMISSION_MODULES.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.key]: { view: false, edit: false, delete: false },
      }),
      {},
    ),
  );
  const [isUserListOpen, setIsUserListOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchText, setUserSearchText] = useState("");
  const userListRef = useRef(null);
  const [popupState, setPopupState] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });
  const { allCustomers } = useSelector((state) => state.customer);
  useEffect(() => {
    if (popupState.open) {
      const timer = setTimeout(() => {
        closePopup();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [popupState.open]);

  useEffect(() => {
    if (open && editData) {
      // Set values for edit mode
      if (editData.employeeId) {
        setSelectedUsers([editData.employeeId]);
      }

      // Map project objects to IDs
      const projIds = (editData.scope?.projectIds || []).map((p) =>
        typeof p === "string" ? p : p._id,
      );
      setSelectedProjects(projIds);

      // Map permissions from array to state object
      const newPerms = PERMISSION_MODULES.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.key]: { view: false, edit: false, delete: false },
        }),
        {},
      );

      (editData.permissions || []).forEach((p) => {
        if (newPerms[p.module]) {
          newPerms[p.module] = {
            view: p.access === "read" || p.access === "write",
            edit: p.access === "write",
            delete: p.access === "write",
          };
        }
      });
      setPermissions(newPerms);
    } else if (open && !editData) {
      // Reset values for create mode
      setSelectedUsers([]);
      setSelectedProjects([]);
      setPermissions(
        PERMISSION_MODULES.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.key]: { view: false, edit: false, delete: false },
          }),
          {},
        ),
      );
      setUserSearchText("");
    }
  }, [open, editData]);

  //dropdown close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userListRef.current && !userListRef.current.contains(event.target)) {
        setIsUserListOpen(false);
      }
    };

    if (isUserListOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserListOpen]);

  useEffect(() => {
    if (open && clientId) {
      fetchUsers("");
    }
  }, [open, clientId]);

  const fetchUsers = async (search = "") => {
    setLoadingUsers(true);
    try {
      // Using GET_ALL_CUSTOMER_USER instead of missing GET_ALL_USER_BY_CLIENT_ID
      const response = await getApi({
        apiUrl: API_URLS.GET_ALL_CUSTOMER_USER(
          1,
          50,
          "-createdAt",
          clientId,
          search,
        ),
        headers: { client: clientId },
      });
      if (response && response.success) {
        setAvailableUsers(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const getUserName = (user) =>
    user?.name || user?.employeeId?.name || user?.employee_id || "N/A";

  const getUserEmail = (user) =>
    user?.email || user?.employeeId?.email || user?.email_id || "";

  const filteredAvailableUsers = useMemo(() => {
    const search = userSearchText.trim().toLowerCase();

    if (!search) return availableUsers;

    return availableUsers.filter((user) => {
      const name = String(getUserName(user)).toLowerCase();
      const email = String(getUserEmail(user)).toLowerCase();
      const employeeCode = String(
        user?.employeeId?.employee_id || "",
      ).toLowerCase();
      return (
        name.includes(search) ||
        email.includes(search) ||
        employeeCode.includes(search)
      );
    });
  }, [availableUsers, userSearchText]);

  const handleProjectToggle = (projectId) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter((id) => id !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };

  const licensesPerUser = selectedProjects.length;
  let totalLicensesRequired = 0;
  if (selectedUsers.length > 0) {
    totalLicensesRequired = licensesPerUser <= 1 ? 1 : licensesPerUser;
  }

  const handlePermissionChange = (moduleKey, type) => {
    setPermissions((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [type]: !prev[moduleKey][type],
      },
    }));
  };

  const getActivePermissions = () => {
    const results = [];
    PERMISSION_MODULES.forEach((module) => {
      const p = permissions[module.key];
      if (p.edit || p.delete) {
        results.push({ module: module.key, access: "write" });
      } else if (p.view) {
        results.push({ module: module.key, access: "read" });
      }
    });
    return results;
  };

  const showPopup = (type, title, popupMessage) => {
    setPopupState({
      open: true,
      type,
      title,
      message: popupMessage,
    });
  };

  const closePopup = () => {
    setPopupState((prev) => ({ ...prev, open: false }));
  };

  const handleSave = async () => {
    if (selectedUsers.length === 0) return;

    setLoadingUsers(true);
    try {
      if (editData) {
        // Edit mode (PUT)
        const payload = {
          projectIds: selectedProjects,
          permissions: getActivePermissions(),
        };
        const res = await putApi({
          apiUrl: API_URLS.UPDATE_SUB_ADMIN(
            clientId,
            editData.employeeId?._id || editData._id,
          ),
          data: payload,
        });
        if (res && res.success) {
          showPopup("success", "Success", "Sub-admin updated successfully");
          onCancel();
          if (refresh) refresh();
          dispatch(fetchCustomerById(clientId));
        } else {
          showPopup("error", "Error", "Failed to update sub-admin");
        }
      } else {
        // Create mode (POST)
        const promises = selectedUsers.map((u) => {
          const payload = {
            employeeId: u._id,
            projectScope: selectedProjects,
            permissions: getActivePermissions(),
          };
          return postApi({
            apiUrl: API_URLS.CREATE_SUB_ADMIN(clientId),
            data: payload,
          });
        });

        const results = await Promise.all(promises);
        const allSuccessful = results.every((res) => res && res.success);

        if (allSuccessful) {
          showPopup(
            "success",
            "Success",
            `Successfully assigned ${selectedUsers.length} sub-admin(s)`,
          );
          onCancel();
          if (refresh) refresh();
          dispatch(fetchCustomerById(clientId));
        } else {
          showPopup("error", "Error", "Some sub-admins could not be assigned");
        }
      }
    } catch (error) {
      console.error("Save Error:", error);
      showPopup("error", "Error", "Failed to assign sub-admins");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUsers([user]);
    setUserSearchText("");
    setIsUserListOpen(false);
  };

  const handleRemoveUser = (email) => {
    setSelectedUsers(selectedUsers.filter((u) => getUserEmail(u) !== email));
  };

  const showError = totalLicensesRequired === 0 && selectedUsers.length > 0;

  return (
    <>
      <Modal
        title={
          <div className="pt-4 px-2">
            <h2 className="text-xl font-bold text-gray-900 mb-0">
              Assign Sub-admin
            </h2>
            <p className="text-sm font-normal text-gray-500 mt-1">
              Set up project or account-level access
            </p>
          </div>
        }
        open={open}
        onCancel={onCancel}
        footer={null}
        width={700}
        className="assign-subadmin-modal"
        closeIcon={
          <CloseOutlined className="text-gray-400 hover:text-gray-600" />
        }
      >
        <div className="py-2 px-2 font-lexend">
          {/* Select User Section */}
          <div className="mb-6 relative" ref={userListRef}>
            <label className="block text-sm font-bold text-gray-900 mb-2 font-lexend">
              Select User
            </label>

            <div className="relative">
              <Input
                placeholder="Search by name or email..."
                className={`h-11 rounded-lg border-gray-200 focus:border-[#0087A3] focus:ring-1 focus:ring-[#0087A3]/20 transition-all ${isUserListOpen ? "rounded-b-none border-b-0" : ""}`}
                onClick={() => setIsUserListOpen(true)}
                value={userSearchText}
                onChange={(e) => {
                  setUserSearchText(e.target.value);
                  setIsUserListOpen(true);
                }}
              />

              {/* Dropdown User List */}
              {isUserListOpen && (
                <div className="absolute top-full left-0 w-full bg-white border border-gray-200 border-t-0 rounded-b-lg shadow-lg z-50 overflow-hidden">
                  <div className="max-h-56 overflow-y-auto custom-scrollbar">
                    {loadingUsers ? (
                      <div className="p-4 text-center">
                        <Spin size="small" />
                      </div>
                    ) : (
                      filteredAvailableUsers
                        .filter(
                          (u) =>
                            !selectedUsers.find(
                              (su) => getUserEmail(su) === getUserEmail(u),
                            ),
                        )
                        .map((user, idx) => (
                          <div
                            key={idx}
                            className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 group ${editData ? "pointer-events-none opacity-50" : ""}`}
                            onClick={() => !editData && handleUserSelect(user)}
                          >
                            <div className="text-sm font-bold text-gray-800 group-hover:text-[#0087A3] transition-colors">
                              {getUserName(user)}
                            </div>
                            <div className="text-xs text-gray-400 font-normal">
                              {getUserEmail(user)}
                            </div>
                          </div>
                        ))
                    )}
                    {!loadingUsers && filteredAvailableUsers.length === 0 && (
                      <div className="p-4 text-center text-gray-400 text-xs">
                        No users found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {selectedUsers.length > 0 && (
              <div className="mt-4 flex flex-col gap-2 animate-fadeIn">
                {selectedUsers.map((user, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-[#E6F7FF] border border-[#BAE7FF] rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="bg-[#B5F5EC] text-[#006D75] font-bold text-xs ring-1 ring-[#87E8DE]">
                        {(getUserName(user) || "U")
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900 leading-tight">
                          {getUserName(user)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {getUserEmail(user)}
                        </span>
                      </div>
                    </div>
                    {!editData && (
                      <CloseOutlined
                        className="text-[#0958D9] cursor-pointer hover:text-red-500 transition-colors p-1"
                        onClick={() => handleRemoveUser(getUserEmail(user))}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-start mb-1">
              <div>
                <label className="block text-sm font-bold text-gray-900 font-lexend">
                  Project Access
                </label>
                <p className="text-xs text-gray-400">
                  Required OR Account Access
                </p>
              </div>
                <Tag
                  color="#06513D"
                  className="flex items-center gap-1 border-[#06513D] text-[#06513D] rounded px-2 py-0.5 m-0 font-medium text-[11px]"
                >
                  <ThunderboltFilled className="text-[10px]" /> Per-Project
                </Tag>
            </div>

            <div className="border border-gray-200 rounded-md overflow-hidden relative">
              <div className="h-36 overflow-y-auto py-1 pr-5 custom-scrollbar">
                {projects.length > 0 ? (
                  projects.map((project, idx) => (
                    <div
                      key={idx}
                      className={`flex min-h-10 items-center px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${selectedProjects.includes(project._id) ? "bg-blue-50/30" : ""}`}
                      onClick={() => handleProjectToggle(project._id)}
                    >
                      <Checkbox
                        checked={selectedProjects.includes(project._id)}
                        className="mr-3 custom-checkbox"
                      />
                      <span
                        className={`text-xs leading-5 ${selectedProjects.includes(project._id) ? "text-blue-600 font-medium" : "text-gray-700"}`}
                      >
                        {project.name}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-400 text-xs">
                    No active projects available
                  </div>
                )}
              </div>
              <div className="absolute right-2 top-1 pointer-events-none opacity-40">
                <UpOutlined className="text-[10px]" />
              </div>
              <div className="absolute right-2 bottom-1 pointer-events-none opacity-40">
                <DownOutlined className="text-[10px]" />
              </div>
            </div>
          </div>

          {/* Granular Permissions Section */}
          <div className="mb-6">
            <div className="grid grid-cols-[1fr_72px_72px_72px] items-start px-3 mb-5">
              <h3 className="text-[13px] font-semibold text-gray-500 font-lexend m-0">
                Permissions
              </h3>
              <span className="text-[11px] font-bold text-gray-400 text-center uppercase tracking-wide">
                View
              </span>
              <span className="text-[11px] font-bold text-gray-400 text-center uppercase tracking-wide">
                Edit
              </span>
              <span className="text-[11px] font-bold text-gray-400 text-center uppercase tracking-wide leading-5 break-all">
                Delete
              </span>
            </div>

            <div>
              {PERMISSION_MODULES.map((module) => (
                <div
                  key={module.key}
                  className="grid min-h-[52px] grid-cols-[1fr_72px_72px_72px] items-center px-3 border-b border-gray-50 last:border-0"
                >
                  <span className="text-[13px] font-bold text-gray-800">
                    {module.label}
                  </span>
                  <div className="flex justify-center">
                    <Checkbox
                      checked={permissions[module.key].view}
                      onChange={() =>
                        handlePermissionChange(module.key, "view")
                      }
                      className="custom-checkbox"
                    />
                  </div>
                  <div className="flex justify-center">
                    <Checkbox
                      checked={permissions[module.key].edit}
                      onChange={() =>
                        handlePermissionChange(module.key, "edit")
                      }
                      className="custom-checkbox"
                    />
                  </div>
                  <div className="flex justify-center">
                    <Checkbox
                      checked={permissions[module.key].delete}
                      onChange={() =>
                        handlePermissionChange(module.key, "delete")
                      }
                      className="custom-checkbox"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4 p-4 rounded-xl border  border-[#06513D] bg-[#E6EEEC]">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-[#06513D] rounded-lg flex items-center justify-center">
                <ThunderboltFilled className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-base font-bold text-black  mb-0 leading-tight">
                  {totalLicensesRequired} Licenses Required
                </h3>
                <p className="text-xs text-black  mt-1">
                  {totalLicensesRequired > 0
                    ? "Allocating licenses based on selection"
                    : "Select projects or enable account access"}
                </p>
              </div>
            </div>
            <Divider className="my-3 border-[#06513D]/50" />
            <div className="flex items-center text-[11px] font-bold text-[#06513D] uppercase tracking-wide">
              Available: {customerStats?.unused || 0} /{" "}
              {customerStats?.total || 0} licenses
            </div>
          </div>

          {showError && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-3 text-red-600 animate-fadeIn">
              <WarningOutlined />
              <span className="text-xs font-medium text-red-700">
                Please select at least one project or enable account access
              </span>
            </div>
          )}

          {totalLicensesRequired > (customerStats?.unused || 0) && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-3 text-red-600 animate-fadeIn">
              <WarningOutlined />
              <span className="text-xs font-medium text-red-700">
                Insufficient licenses. You need {totalLicensesRequired} but only{" "}
                {customerStats?.unused || 0} are available.
              </span>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2 pb-1 border-t border-gray-50 mt-4">
            <Button
              className="h-10 px-8 rounded-lg  hover:!bg-[#F8F8F8] hover:!text-[#06513D] border-[#06513D] hover:!border-[#06513D] bg-[#E6EDEB] text-[#06513D] font-bold"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className={`h-10 px-8 rounded-lg font-bold border-none ${
                totalLicensesRequired > 0 &&
                selectedUsers.length > 0 &&
                totalLicensesRequired <= (customerStats?.unused || 0)
                  ? "bg-[#06513D] hover:!bg-[#387464] text-white"
                  : "bg-[#D1D5DB] text-white cursor-not-allowed shadow-none"
              }`}
              loading={loadingUsers}
              onClick={handleSave}
              disabled={
                totalLicensesRequired === 0 ||
                selectedUsers.length === 0 ||
                totalLicensesRequired > (customerStats?.unused || 0)
              }
            >
              Save
            </Button>
          </div>
        </div>

        <style jsx>{`
          .assign-subadmin-modal :global(.ant-modal-content) {
            border-radius: 16px !important;
            padding: 12px 20px !important;
          }
          .assign-subadmin-modal :global(.ant-modal-header) {
            border-bottom: none !important;
            margin-bottom: 8px !important;
          }
          .custom-checkbox :global(.ant-checkbox-inner) {
            border-radius: 4px !important;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 10px;
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </Modal>

      {popupState.open &&
        createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
            <PopupCard
              type={popupState.type}
              title={popupState.title}
              message={popupState.message}
              onConfirm={closePopup}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

const RolesPage = ({ clientId }) => {
  const { user } = useSelector((state) => state.auth);
  const { currentCustomer, allCustomers } = useSelector(
    (state) => state.customer,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [roleType, setRoleType] = useState("All");
  const [status, setStatus] = useState("All");
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [customerStats, setCustomerStats] = useState({
    total: 0,
    used: 0,
    unused: 0,
    status: "N/A",
  });

  // Sync customer stats from currentCustomer or allCustomers in Redux
  useEffect(() => {
    const customer =
      currentCustomer ||
      (allCustomers || []).find((c) => (c._id || c.userId) === clientId);
    if (customer) {
      const subAdminLicense = customer?.license?.sub_admin || {};
      const getVal = (val) => {
        if (val && typeof val === "object")
          return val.total ?? val.used ?? val.unused ?? 0;
        return val || 0;
      };

      setCustomerStats({
        total: getVal(subAdminLicense.total),
        used: getVal(subAdminLicense.used),
        unused: getVal(subAdminLicense.unused),
        status: customer?.status || "N/A",
      });
    }
  }, [currentCustomer, allCustomers, clientId]);

  const fetchCustomerProjects = async () => {
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_CUSTOMER_FILTERED_PROJECTS(
          100,
          0,
          "-createdAt",
          "",
          {},
        ),
        headers: { client: clientId },
      });
      if (response && response.success && response.data) {
        const projectsData = response.data;

        // Filter projects for the modal
        const today = dayjs().date();
        const filtered = (projectsData || []).filter((item) => {
          const endDay = dayjs(item.endDate).date();
          return endDay !== today;
        });
        setProjects(filtered);
      }
    } catch (error) {
      console.error("Error fetching customer projects:", error);
    }
  };

  const [editData, setEditData] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    visible: false,
    subAdminId: null,
    subAdminName: "",
  });
  const [successPopup, setSuccessPopup] = useState({
    visible: false,
    title: "",
    message: "",
  });

  useEffect(() => {
    if (clientId) {
      fetchSubAdmins();
      fetchCustomerProjects();
    }
  }, [clientId]);

  const handleEdit = (record) => {
    setEditData(record);
    setIsModalOpen(true);
  };
  const handleDelete = (record) => {
    setDeleteConfirmation({
      visible: true,
      subAdminId: record.employeeId?._id || record._id,
      subAdminName: getUserName(record),
    });
  };
  const handleAddNew = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const getUserName = (record) =>
    record?.employeeId?.name || record?.name || "N/A";

  const getUserEmail = (record) =>
    record?.employeeId?.email || record?.email || "";

  const getProjectNames = (record) =>
    (record?.scope?.projectIds || [])
      .map((project) => project?.name || "")
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const filteredData = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return data.filter((record) => {
      const matchesStatus =
        status === "All" ||
        String(record?.employeeId?.status || record?.status || "")
          .toLowerCase()
          .includes(status.toLowerCase());

      if (!matchesStatus) return false;

      if (!search) return true;

      const name = getUserName(record).toLowerCase();
      const email = getUserEmail(record).toLowerCase();
      const employeeCode = String(
        record?.employeeId?.employee_id || record?.employee_id || "",
      ).toLowerCase();
      const projects = getProjectNames(record);

      return (
        name.includes(search) ||
        email.includes(search) ||
        employeeCode.includes(search) ||
        projects.includes(search)
      );
    });
  }, [data, searchTerm, status]);

  const fetchSubAdmins = async () => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_SUB_ADMINS(clientId),
      });
      if (response && response.success) {
        setData(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching sub-admins:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "NAME",
      dataIndex: ["employeeId", "name"],
      key: "name",
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <Avatar className="bg-blue-50 text-blue-600 font-bold text-xs ring-1 ring-blue-100">
            {getUserName(record)
              ? getUserName(record)
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
              : "U"}
          </Avatar>
          <span className="font-semibold text-gray-800">
            {getUserName(record)}
          </span>
        </div>
      ),
    },
    {
      title: "EMAIL",
      dataIndex: ["employeeId", "email"],
      key: "email",
      render: (text, record) => (
        <span className="text-gray-500">{getUserEmail(record)}</span>
      ),
    },
    {
      title: "PROJECTS",
      dataIndex: ["scope", "projectIds"],
      key: "projects",
      render: (_, record) => {
        const projectNames = (record.scope?.projectIds || [])
          .map((project) => project?.name)
          .filter(Boolean);

        return (
          <div className="text-gray-600 max-w-[320px] whitespace-normal break-words">
            {projectNames.length > 0 ? projectNames.join(", ") : "N/A"}
          </div>
        );
      },
    },
    {
      title: "ACCOUNT ACCESS",
      key: "accountAccess",
      render: (_, record) => {
        const hasAccountAccess = (record.permissions || []).length > 0;
        return (
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium border ${
              hasAccountAccess
                ? "bg-green-50 text-green-700 border-green-100"
                : "bg-blue-50 text-blue-700 border-blue-100"
            }`}
          >
            {hasAccountAccess ? (
              <CheckOutlined className="text-[10px]" />
            ) : (
              <CloseOutlined className="text-[10px]" />
            )}
            {hasAccountAccess ? "Yes" : "No"}
          </div>
        );
      },
    },
    {
      title: "LICENSES USED",
      key: "licensesUsed",
      render: (_, record) => {
        const projectCount = record.scope?.projectIds?.length || 0;
         const count = projectCount > 1 ? projectCount : 1;
        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 bg-blue-50 text-blue-500 rounded border border-blue-100">
              ✧
            </div>
            <span className="font-medium text-gray-700">{count}</span>
          </div>
        );
      },
    },
    {
      title: "STATUS",
      dataIndex: ["employeeId", "status"],
      key: "status",
      render: (status) => (
        <div
          className={`inline-block px-3 py-0.5 rounded-full text-[11px] font-bold ${
            status === "Active"
              ? "bg-green-50 text-green-700"
              : "bg-red-50 text-red-700"
          }`}
        >
          {status || "Unknown"}
        </div>
      ),
    },
    {
      title: "ACTION",
      key: "action",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <EditOutlined
            className="text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
            onClick={() => handleEdit(record)}
          />
          <DeleteIcon
           className="cursor-pointer"
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div
      style={{ paddingLeft: "40px", paddingRight: "40px", paddingTop: "20px" }}
    >
      <CardsBorder>
        <div className="p-6 font-lexend">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              {
                title: "Total Licenses",
                value: customerStats.total || "0",
                icon: <UserOutlined />,
                color: "blue",
              },
              {
                title: "Used Licenses",
                value: customerStats.used || "0",
                icon: <KeyOutlined />,
                color: "emerald",
              },
              {
                title: "Available",
                value: customerStats.unused || "0",
                icon: <CheckCircleOutlined />,
                color: "green",
              },
              {
                title: "Status",
                value: customerStats.status || "N/A",
                icon: <CheckCircleOutlined />,
                color: "green",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col items-center"
              >
                <div
                  className={`w-12 h-12 rounded-lg mb-3 flex items-center justify-center bg-${card.color}-50 text-${card.color}-600`}
                >
                  {React.cloneElement(card.icon, { className: "text-2xl" })}
                </div>
                <div className="text-3xl font-extrabold text-gray-900">
                  {card.value}
                </div>
                <div className="text-xs text-gray-400 mt-2 uppercase tracking-wide font-medium">
                  {card.title}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 mb-6">
            <div className="flex flex-col lg:flex-row items-end gap-4">
              <div className="flex-1 w-full">
                <Input
                  placeholder="Search by name or email..."
                  prefix={<SearchOutlined className="text-gray-400" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-[42px] rounded-lg border-gray-200"
                />
              </div>

              <div className="w-full lg:w-48">
                <label className="text-xs text-gray-400 mb-1 block">
                  Status
                </label>
                <Select
                  value={status}
                  onChange={setStatus}
                  className="w-full h-[42px] [&&>.ant-select-selector]:rounded-lg"
                >
                  <Option value="All">All</Option>
                  <Option value="Active">Active</Option>
                  <Option value="Revoked">Revoked</Option>
                </Select>
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="h-[42px] px-6 rounded-lg bg-[#06513D] hover:!bg-[#387464] border-none font-bold whitespace-nowrap"
                onClick={handleAddNew}
              >
                Add sub-admin
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <Table
              columns={columns}
              dataSource={filteredData}
              pagination={false}
              loading={loading}
              rowKey="_id"
              className="[&_.ant-table-thead_th]:bg-gray-50/50 [&_.ant-table-thead_th]:text-gray-400 [&_.ant-table-thead_th]:text-[11px] [&_.ant-table-thead_th]:font-bold [&_.ant-table-thead_th]:uppercase [&_.ant-table-thead_th]:tracking-wider [&_.ant-table-thead_th]:border-b-gray-100"
              rowClassName={() =>
                "hover:bg-gray-50/50 transition-colors cursor-pointer"
              }
            />
          </div>
        </div>
      </CardsBorder>

      <AssignSubAdminModal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditData(null);
        }}
        refresh={fetchSubAdmins}
        refreshStats={fetchCustomerProjects}
        projects={projects}
        customerStats={customerStats}
        editData={editData}
        clientId={clientId}
      />

       {/* Delete Confirmation */}
      {deleteConfirmation.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="warning"
            title="Confirm Delete"
            message={`Are you sure you want to delete ${deleteConfirmation.subAdminName}?`}
            showActions={true}
            confirmText="Yes, Delete"
            cancelText="Cancel"
            onCancel={() =>
              setDeleteConfirmation({
                visible: false,
                subAdminId: null,
                subAdminName: "",
              })
            }
            onConfirm={async () => {
              try {
                const response = await deleteApi({
                  apiUrl: API_URLS.DELETE_SUB_ADMIN(clientId, deleteConfirmation.subAdminId),
                });
                if (response && response.success) {
                  setDeleteConfirmation({
                    visible: false,
                    subAdminId: null,
                    subAdminName: "",
                  });
                  setSuccessPopup({
                    visible: true,
                    title: "Deleted",
                    message: "Sub-admin deleted successfully!",
                  });
                  setTimeout(() => {
                    setSuccessPopup({
                      visible: false,
                      title: "",
                      message: "",
                    });
                  }, 3000);
                  fetchSubAdmins();
                  // dispatch(fetchCustomerById(clientId));
                } else {
                  console.error("Delete failed:", response?.message);
                }
              } catch (err) {
                console.error("Delete error:", err);
              }
            }}
          />
        </div>
      )}
      {/* Success Popup */}
      {successPopup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="success"
            title={successPopup.title}
            message={successPopup.message}
          />
        </div>
      )}
    </div>
  );
};

export default RolesPage;
